/**
 * SecondFactorGate
 *
 * Right after sign-in: when one of the person's tenants requires a second
 * factor and this session was signed in with a password alone, ask for an
 * authenticator app code — or, the first time, set the app up. The database
 * already refuses such a session every workspace in that tenant (migration
 * 986); this screen is what the person sees instead of empty pages.
 *
 * If the requirement cannot be read, the app goes on: the database is still
 * the check.
 */

import { useEffect, useState } from 'react';
import { PrioritiesLogo } from '../ui/PrioritiesLogo';
import { btnSecondary } from '../mono/primitives';
import { useUserStore } from '../../stores/prioritization/userStore';
import { fetchSecondFactorRequirement } from '../../lib/identityService';
import { AuthenticatorCodeForm } from './AuthenticatorCodeForm';

type GateState = { step: 'checking' } | { step: 'open' } | { step: 'ask'; tenants: string[] };

function tenantWords(tenants: string[]): string {
  if (tenants.length === 0) return 'Your organization';
  if (tenants.length === 1) return tenants[0];
  return `${tenants.slice(0, -1).join(', ')} and ${tenants[tenants.length - 1]}`;
}

export function SecondFactorGate({ children }: { children: React.ReactNode }) {
  const userId = useUserStore((s) => s.userId);
  const signOut = useUserStore((s) => s.signOut);
  const [state, setState] = useState<GateState>({ step: 'checking' });
  const [mode, setMode] = useState<'enter' | 'set-up'>('enter');

  useEffect(() => {
    let cancelled = false;
    fetchSecondFactorRequirement()
      .then((req) => {
        if (!cancelled) setState(req.required && req.level !== 'aal2' ? { step: 'ask', tenants: req.tenants } : { step: 'open' });
      })
      .catch((err) => {
        console.warn('[SecondFactorGate] could not read the sign-in requirement', err);
        if (!cancelled) setState({ step: 'open' });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // While the requirement is read the app renders as usual: nearly everyone
  // needs no code, and holding every page load for one call would cost them
  // all a pause. Someone who does need one sees empty pages for a moment.
  if (state.step !== 'ask') return <>{children}</>;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <PrioritiesLogo height={34} />
        </div>
        <div
          className="bg-[var(--bg-raised)] border border-[var(--rule)] border-t-[3px] p-8 sm:p-10"
          style={{ borderTopColor: 'var(--mustard)' }}
        >
          <h1 className="font-serif text-3xl font-light leading-tight text-[var(--text-pri)]">
            {mode === 'enter' ? 'Enter your code' : 'Set up a sign-in code'}
          </h1>
          <div className="mt-5 mb-6 h-px w-9" style={{ backgroundColor: 'var(--mustard)' }} />
          <p className="mb-6 text-sm leading-relaxed text-[var(--text-sec)]">
            {tenantWords(state.tenants)} asks everyone to sign in with a code from an authenticator app as well as their password.
          </p>
          <AuthenticatorCodeForm
            onMode={setMode}
            // Every store loaded so far read with the password-only session,
            // which the database showed nothing; start again with the new one.
            onVerified={() => window.location.reload()}
          />
          <div className="mt-6 pt-6 border-t border-[var(--rule)] flex justify-center">
            <button type="button" {...btnSecondary()} onClick={() => signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
